import * as Sentry from '@sentry/react';
import { getEnv } from 'util/getEnv';
import { getLanguageKey } from 'utils';
import { grecaptcha } from 'util/grecaptcha';
import {
  awaitReloadIfPending,
  recordRecaptchaFailure,
  resetRecaptchaFailures,
} from 'util/recaptcha-reload';
import { isRecaptchaHardeningEnabled } from 'util/recaptchaHardening';

const RECAPTCHA_ATTEMPT_TIMEOUT_MS = 5000;
const RECAPTCHA_MAX_ATTEMPTS = 2;

const executeWithTimeout = (gre, key, action) =>
  Promise.race([
    gre.enterprise.execute(key, { action }),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('reCAPTCHA client timeout')),
        RECAPTCHA_ATTEMPT_TIMEOUT_MS,
      ),
    ),
  ]);

const defaultActivityContent = () => ({
  messageList: {
    messages: [],
  },
  offerList: {
    offers: [],
  },
});

export const deleteActivityContent = (content, id) => {
  const baseActivityContent = defaultActivityContent();

  if (!content) {
    return undefined;
  }

  baseActivityContent.offerList.offers = content.offerList.offers.filter(
    offer => offer.distributedOfferId !== id,
  );
  baseActivityContent.messageList.messages =
    content.messageList.messages.filter(
      message => message.distributedMessageId !== id,
    );

  return baseActivityContent;
};

export const getActivityEvents = (messageId, socialMedia, checkIn) => {
  let smEvent;

  if (!!socialMedia) {
    switch (socialMedia) {
      case 'tw':
        smEvent = { 'socialmedia:tw': [`MSG:${messageId}`] };
        break;

      case 'fb':
        smEvent = { 'socialmedia:fb': [`MSG:${messageId}`] };
        break;

      default:
        smEvent = {};
    }
  }

  return smEvent ? { ...smEvent } : { 'check-in': [checkIn] };
};

const legacyGetRecaptchaToken = async action => {
  const gre = grecaptcha();
  const recaptchaKey = getEnv('REACT_APP_RECAPTCHA_KEY');
  if (!gre?.enterprise) return null;

  try {
    await new Promise(resolve => gre.enterprise.ready(resolve));
    return await gre.enterprise.execute(recaptchaKey, { action });
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

const hardenedGetRecaptchaToken = async action => {
  await awaitReloadIfPending();

  const gre = grecaptcha();
  const recaptchaKey = getEnv('REACT_APP_RECAPTCHA_KEY');
  if (!gre?.enterprise) return null;

  try {
    await new Promise(resolve => gre.enterprise.ready(resolve));
  } catch (e) {
    Sentry.captureException(e);
    recordRecaptchaFailure();
    return null;
  }

  let lastError;
  for (let attempt = 0; attempt < RECAPTCHA_MAX_ATTEMPTS; attempt += 1) {
    try {
      const token = await executeWithTimeout(gre, recaptchaKey, action);
      resetRecaptchaFailures();
      return token;
    } catch (e) {
      lastError = e;
    }
  }

  Sentry.captureException(lastError, {
    level: 'warning',
    tags: { recaptcha_path: 'hardened_exhausted' },
  });
  recordRecaptchaFailure();
  return null;
};

export const getRecaptchaToken = action =>
  isRecaptchaHardeningEnabled()
    ? hardenedGetRecaptchaToken(action)
    : legacyGetRecaptchaToken(action);

export const buildSignUpBody = formData => {
  const {
    email,
    firstName,
    EMAIL_OPT_IN,
    DIETARY_PREFERENCES,
    ALLERGIES,
    lastName,
    password,
    phoneNumber,
    referralCode,
    dateOfBirth,
    authenticationProvider,
    jwt,
    SMS_MARKETING_OPT_IN,
    SMS_TXN_OPT_IN,
  } = formData;

  return {
    baseLanguagePreferences: getLanguageKey().toUpperCase(),
    email,
    extendedAttributes: {
      USER_AFFILIATION: 'CUSTOMER',
      EMAIL_OPT_IN: EMAIL_OPT_IN.toString().toUpperCase(),
      DIETARY_PREFERENCES:
        DIETARY_PREFERENCES?.length > 0
          ? DIETARY_PREFERENCES?.toString().toUpperCase()
          : undefined,
      ALLERGIES:
        ALLERGIES?.length > 0 ? ALLERGIES?.toString().toUpperCase() : undefined,
      SMS_TXN_OPT_IN: SMS_TXN_OPT_IN
        ? SMS_TXN_OPT_IN.toString().toUpperCase()
        : 'FALSE',
      SMS_MARKETING_OPT_IN: SMS_MARKETING_OPT_IN
        ? SMS_MARKETING_OPT_IN.toString().toUpperCase()
        : 'FALSE',
    },
    firstName,
    howAcquired: 'THROUGH_WEB',
    lastName,
    password,
    phoneNumber,
    referralCode,
    dateOfBirth,
    authenticationProvider,
    jwt,
  };
};
